import type { DetailedPermitStage, Job, PermitDetail } from "@/data/types";
import { hashFilters } from "@/lib/queryKeys";
import { observeQueryStart, recordQueryMetric } from "@/lib/queryObservability";
import { supabase } from "@/lib/supabase";
import { toUiJob } from "@/mappers/job.mapper";
import type { JobRow } from "@/services/job.service";

export interface PageResponse<T> {
  data: T[];
  count: number;
  error: unknown | null;
}

export type PermittingQuickView =
  | "all"
  | "stalled"
  | "pending"
  | "needs-prep"
  | "approved"
  | "rev-critical"
  | "rev-ready"
  | "rev-waiting"
  | "ins-active"
  | "ins-failed"
  | "ins-ready"
  | "ins-awaiting"
  | "close-ready"
  | "close-pending"
  | "close-not-started"
  | "prep-awaiting-sig"
  | "prep-awaiting-notarization"
  | "prep-ready-for-submission";

export interface PermittingJobsPageQuery {
  page: number;
  pageSize: number;
  search?: string;
  stage?: DetailedPermitStage | "All";
  quickView?: PermittingQuickView;
  branch?: string;
  region?: string;
  refreshToken?: number;
}

export interface PermittingSummary {
  totalPermits: number;
  stalled: number;
  pendingApproval: number;
  needsPrep: number;
  approved: number;
  revCritical: number;
  revReady: number;
  revWaiting: number;
  insActive: number;
  insFailed: number;
  insReady: number;
  insAwaiting: number;
  closeReady: number;
  closePending: number;
  closeNotStarted: number;
  stageCounts: Record<DetailedPermitStage, number>;
  branches: string[];
  regions: string[];
}

type PermittingJobViewRow = {
  organization_id?: string | null;
  id: string;
  source_meta?: Job["sourceMeta"] | null;
  current_stage?: string | null;
  title?: string | null;
  job_type?: string | null;
  job_number?: string | null;
  customer_id?: string | null;
  contractor_id?: string | null;
  crew_id?: string | null;
  scheduled_start_at?: string | null;
  scheduled_end_at?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  material_readiness?: string | null;
  permit_required?: boolean | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  updated_at?: string | null;
  customer_first_name?: string | null;
  customer_last_name?: string | null;
  customer_address_line1?: string | null;
  customer_address_line2?: string | null;
  customer_city?: string | null;
  customer_state?: string | null;
  customer_postal_code?: string | null;
  customer_name?: string | null;
  project_type?: string | null;
  branch?: string | null;
  region?: string | null;
  sales_rep?: string | null;
  permit_status?: string | null;
  permit_detail?: Record<string, unknown> | null;
  permit_stage?: DetailedPermitStage | null;
  permit_stage_sort?: number | null;
  municipality?: string | null;
  permit_type?: string | null;
  submitted_date?: string | null;
  last_update?: string | null;
  assigned_coordinator?: string | null;
  blockers?: string | null;
  target_approval_date?: string | null;
  is_stalled?: boolean | null;
  is_pending_approval?: boolean | null;
  is_needs_prep?: boolean | null;
  is_approved_stage?: boolean | null;
  is_rev_critical?: boolean | null;
  is_rev_ready?: boolean | null;
  is_rev_waiting?: boolean | null;
  is_ins_active?: boolean | null;
  is_ins_failed?: boolean | null;
  is_ins_ready?: boolean | null;
  is_ins_awaiting?: boolean | null;
  is_close_ready?: boolean | null;
  is_close_pending?: boolean | null;
  is_close_not_started?: boolean | null;
  sort_at?: string | null;
};

type PermittingSummaryViewRow = {
  total_permits?: number | null;
  stalled?: number | null;
  pending_approval?: number | null;
  needs_prep?: number | null;
  approved?: number | null;
  rev_critical?: number | null;
  rev_ready?: number | null;
  rev_waiting?: number | null;
  ins_active?: number | null;
  ins_failed?: number | null;
  ins_ready?: number | null;
  ins_awaiting?: number | null;
  close_ready?: number | null;
  close_pending?: number | null;
  close_not_started?: number | null;
  stage_permit_required?: number | null;
  stage_preparing_documents?: number | null;
  stage_submitted?: number | null;
  stage_under_review?: number | null;
  stage_revision_requested?: number | null;
  stage_resubmitted?: number | null;
  stage_approved?: number | null;
  stage_closed?: number | null;
  branches?: string[] | null;
  regions?: string[] | null;
};

const JOB_COLUMNS = [
  "organization_id",
  "id",
  "source_meta",
  "current_stage",
  "title",
  "job_type",
  "job_number",
  "customer_id",
  "contractor_id",
  "crew_id",
  "scheduled_start_at",
  "scheduled_end_at",
  "address_line1",
  "address_line2",
  "city",
  "state",
  "postal_code",
  "material_readiness",
  "permit_required",
  "notes",
  "metadata",
  "created_at",
  "updated_at",
  "customer_first_name",
  "customer_last_name",
  "customer_address_line1",
  "customer_address_line2",
  "customer_city",
  "customer_state",
  "customer_postal_code",
  "customer_name",
  "project_type",
  "branch",
  "region",
  "sales_rep",
  "permit_status",
  "permit_detail",
  "permit_stage",
  "permit_stage_sort",
  "municipality",
  "permit_type",
  "submitted_date",
  "last_update",
  "assigned_coordinator",
  "blockers",
  "target_approval_date",
  "is_stalled",
  "is_pending_approval",
  "is_needs_prep",
  "is_approved_stage",
  "is_rev_critical",
  "is_rev_ready",
  "is_rev_waiting",
  "is_ins_active",
  "is_ins_failed",
  "is_ins_ready",
  "is_ins_awaiting",
  "is_close_ready",
  "is_close_pending",
  "is_close_not_started",
  "sort_at",
].join(", ");

const SUMMARY_COLUMNS = [
  "total_permits",
  "stalled",
  "pending_approval",
  "needs_prep",
  "approved",
  "rev_critical",
  "rev_ready",
  "rev_waiting",
  "ins_active",
  "ins_failed",
  "ins_ready",
  "ins_awaiting",
  "close_ready",
  "close_pending",
  "close_not_started",
  "stage_permit_required",
  "stage_preparing_documents",
  "stage_submitted",
  "stage_under_review",
  "stage_revision_requested",
  "stage_resubmitted",
  "stage_approved",
  "stage_closed",
  "branches",
  "regions",
].join(", ");

const QUICK_VIEW_FILTERS: Partial<Record<PermittingQuickView, keyof PermittingJobViewRow>> = {
  stalled: "is_stalled",
  pending: "is_pending_approval",
  "needs-prep": "is_needs_prep",
  approved: "is_approved_stage",
  "rev-critical": "is_rev_critical",
  "rev-ready": "is_rev_ready",
  "rev-waiting": "is_rev_waiting",
  "ins-active": "is_ins_active",
  "ins-failed": "is_ins_failed",
  "ins-ready": "is_ins_ready",
  "ins-awaiting": "is_ins_awaiting",
  "close-ready": "is_close_ready",
  "close-pending": "is_close_pending",
  "close-not-started": "is_close_not_started",
};

function num(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function recordPageMetric(key: string, startedAt: number, payload: unknown) {
  let payloadBytes = 0;
  try {
    payloadBytes = JSON.stringify(payload ?? null).length;
  } catch {
    payloadBytes = 0;
  }
  recordQueryMetric({
    key,
    durationMs: Date.now() - startedAt,
    payloadBytes,
    cacheHit: false,
  });
}

async function withPageMetrics<T>(key: string, run: () => Promise<T>): Promise<T> {
  observeQueryStart(key);
  const startedAt = Date.now();
  try {
    const result = await run();
    recordPageMetric(key, startedAt, result);
    return result;
  } catch (error) {
    recordPageMetric(key, startedAt, { error: String(error) });
    throw error;
  }
}

function defaultPermitDetail(row: PermittingJobViewRow): PermitDetail {
  return {
    permitType: row.permit_type ?? "",
    municipality: row.municipality ?? row.city ?? "",
    submittedDate: row.submitted_date ?? "",
    detailedStage: row.permit_stage ?? "Permit Required",
    lastUpdate: row.last_update ?? row.updated_at ?? row.created_at ?? "",
    permitNotes: "",
    assignedCoordinator: row.assigned_coordinator ?? row.sales_rep ?? "",
    blockers: row.blockers ?? "",
    targetApprovalDate: row.target_approval_date ?? "",
  };
}

function normalizePermitDetail(row: PermittingJobViewRow): PermitDetail {
  const raw = row.permit_detail && typeof row.permit_detail === "object"
    ? row.permit_detail
    : {};
  const fallback = defaultPermitDetail(row);
  return {
    ...fallback,
    ...raw,
    detailedStage: (raw.detailedStage as DetailedPermitStage | undefined) ?? fallback.detailedStage,
    permitType: String(raw.permitType ?? fallback.permitType),
    municipality: String(raw.municipality ?? fallback.municipality),
    submittedDate: String(raw.submittedDate ?? fallback.submittedDate),
    lastUpdate: String(raw.lastUpdate ?? fallback.lastUpdate),
    permitNotes: String(raw.permitNotes ?? fallback.permitNotes),
    assignedCoordinator: String(raw.assignedCoordinator ?? fallback.assignedCoordinator),
    blockers: String(raw.blockers ?? fallback.blockers),
    targetApprovalDate: String(raw.targetApprovalDate ?? fallback.targetApprovalDate),
  } as PermitDetail;
}

function mapJob(row: PermittingJobViewRow): Job {
  const permitDetail = normalizePermitDetail(row);
  const metadata = {
    ...(row.metadata ?? {}),
    customerName: row.customer_name ?? (row.metadata?.customerName as string | undefined),
    projectType: row.project_type ?? (row.metadata?.projectType as string | undefined),
    branch: row.branch ?? (row.metadata?.branch as string | undefined),
    region: row.region ?? (row.metadata?.region as string | undefined),
    salesRep: row.sales_rep ?? (row.metadata?.salesRep as string | undefined),
    permitStatus: row.permit_status ?? (row.metadata?.permitStatus as string | undefined),
    permitDetail,
  };

  return toUiJob({
    id: row.id,
    organization_id: row.organization_id ?? null,
    source_meta: row.source_meta ?? null,
    current_stage: row.current_stage ?? null,
    title: row.title ?? null,
    job_type: row.job_type ?? row.project_type ?? null,
    job_number: row.job_number ?? null,
    customer_id: row.customer_id ?? null,
    contractor_id: row.contractor_id ?? null,
    crew_id: row.crew_id ?? null,
    scheduled_start_at: row.scheduled_start_at ?? null,
    scheduled_end_at: row.scheduled_end_at ?? null,
    address_line1: row.address_line1 ?? null,
    address_line2: row.address_line2 ?? null,
    city: row.city ?? null,
    state: row.state ?? null,
    postal_code: row.postal_code ?? null,
    material_readiness: row.material_readiness ?? null,
    permit_required: row.permit_required ?? true,
    notes: row.notes ?? null,
    metadata,
    customers: {
      first_name: row.customer_first_name ?? null,
      last_name: row.customer_last_name ?? null,
      address_line1: row.customer_address_line1 ?? null,
      address_line2: row.customer_address_line2 ?? null,
      city: row.customer_city ?? null,
      state: row.customer_state ?? null,
      postal_code: row.customer_postal_code ?? null,
    },
    created_at: row.created_at ?? new Date().toISOString(),
    updated_at: row.updated_at ?? row.created_at ?? new Date().toISOString(),
  } as JobRow);
}

function mapSummary(row: PermittingSummaryViewRow | null | undefined): PermittingSummary {
  return {
    totalPermits: num(row?.total_permits),
    stalled: num(row?.stalled),
    pendingApproval: num(row?.pending_approval),
    needsPrep: num(row?.needs_prep),
    approved: num(row?.approved),
    revCritical: num(row?.rev_critical),
    revReady: num(row?.rev_ready),
    revWaiting: num(row?.rev_waiting),
    insActive: num(row?.ins_active),
    insFailed: num(row?.ins_failed),
    insReady: num(row?.ins_ready),
    insAwaiting: num(row?.ins_awaiting),
    closeReady: num(row?.close_ready),
    closePending: num(row?.close_pending),
    closeNotStarted: num(row?.close_not_started),
    stageCounts: {
      "Not Required": 0,
      "Permit Required": num(row?.stage_permit_required),
      "Preparing Documents": num(row?.stage_preparing_documents),
      Submitted: num(row?.stage_submitted),
      "Under Review": num(row?.stage_under_review),
      "Revision Requested": num(row?.stage_revision_requested),
      Resubmitted: num(row?.stage_resubmitted),
      Approved: num(row?.stage_approved),
      Closed: num(row?.stage_closed),
    },
    branches: Array.isArray(row?.branches) ? row.branches.filter(Boolean) : [],
    regions: Array.isArray(row?.regions) ? row.regions.filter(Boolean) : [],
  };
}

function sanitizedSearchPattern(search: string): string {
  return `%${search.trim().replace(/[%_]/g, "").replace(/,/g, " ")}%`;
}

export const PermittingPageService = {
  async getJobsPage(query: PermittingJobsPageQuery): Promise<PageResponse<Job>> {
    const key = `permitting.jobs-page.${hashFilters(query)}`;
    try {
      return await withPageMetrics(key, async () => {
        const from = Math.max(0, query.page * query.pageSize);
        const to = from + Math.max(1, query.pageSize) - 1;
        let request = supabase
          .from("v_permitting_jobs_ui")
          .select(JOB_COLUMNS, { count: "exact" })
          .order("permit_stage_sort", { ascending: true })
          .order("sort_at", { ascending: false })
          .order("id", { ascending: true })
          .range(from, to);

        const search = query.search?.trim();
        if (search) {
          const pattern = sanitizedSearchPattern(search);
          request = request.or([
            `customer_name.ilike.${pattern}`,
            `job_number.ilike.${pattern}`,
            `city.ilike.${pattern}`,
            `municipality.ilike.${pattern}`,
            `project_type.ilike.${pattern}`,
          ].join(","));
        }
        if (query.stage && query.stage !== "All") request = request.eq("permit_stage", query.stage);
        if (query.branch && query.branch !== "all") request = request.eq("branch", query.branch);
        if (query.region && query.region !== "all") request = request.eq("region", query.region);
        const quickFilter = query.quickView && query.quickView !== "all"
          ? QUICK_VIEW_FILTERS[query.quickView]
          : undefined;
        if (quickFilter) request = request.eq(quickFilter, true);

        const { data, error, count } = await request;
        if (error) throw error;
        return { data: ((data ?? []) as PermittingJobViewRow[]).map(mapJob), count: count ?? 0, error: null };
      });
    } catch (error) {
      console.warn("PermittingPageService.getJobsPage failed:", error);
      return { data: [], count: 0, error };
    }
  },

  async getSummary(refreshToken?: number): Promise<{ data: PermittingSummary | null; error: unknown | null }> {
    const key = `permitting.summary.${hashFilters({ refreshToken })}`;
    try {
      return await withPageMetrics(key, async () => {
        const { data, error } = await supabase
          .from("v_permitting_summary")
          .select(SUMMARY_COLUMNS)
          .maybeSingle();
        if (error) throw error;
        return { data: mapSummary(data as PermittingSummaryViewRow | null), error: null };
      });
    } catch (error) {
      console.warn("PermittingPageService.getSummary failed:", error);
      return { data: null, error };
    }
  },
};
